import InsertChartOutlinedTwoToneIcon from '@mui/icons-material/InsertChartOutlinedTwoTone';
import { useCallback, useEffect, useState } from 'react';
import {
  IComposableDashboardCollection,
  IComposableDashboardCollectionsContext,
  IPinnedDashboardCollection,
  IUserDTO,
} from 'wisestep-api';

import { useUserContext, useWorkspaceContext } from '../../../context';
import { useComposableAnalyticsDataAccessHook } from '../../../hooks/useComposableAnalyticsDataAccessHook';
import { MAIL_ART_WORKSPACE_ID } from '../../../utils/Constants';
import { jobAnalyticsOptions } from '../analyticsOptions/AnalyticsSubOptions';
import { AnalyticsMenuItems, IAnalyticsMenuOption } from '../types';

import { useComposableDashboardCollectionsContext } from './useComposableDashboardCollectionsContext';

const useCollectionsOptions = (
  handleOnClick: (
    index: number,
    action:
      | 'pin'
      | 'unpin'
      | 'assign'
      | 'share'
      | 'rename'
      | 'delete'
      | 'tag'
      | 'assignToClients'
      | 'unAssignToClients'
      | 'assignToClientSpoc'
      | 'unAssignToClientSpoc'
      | 'assignToAccountManagerOU'
      | 'unAssignToAccountManagerOU'
      | 'assignToTeamsOU'
      | 'unAssignToTeamsOU'
      | 'assignToTeams'
      | 'unAssignToTeams'
      | 'assignToAccountManagers'
      | 'unAssignToAccountManagers'
      | 'assignToBusinessUnit'
      | 'unAssignToBusinessUnit'
      | 'assignToClientUnit'
      | 'unAssignToClientUnit'
      | 'assignToClientContactUnit'
      | 'unAssignToClientContactUnit'
      | 'assignToUsers'
      | 'unAssignToUsers'
      | 'assignToJobs'
      | 'unAssignToJobs',
    creator?: boolean
  ) => Promise<void>,
  pinnedDashboards: IPinnedDashboardCollection[]
) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspace = useWorkspaceContext();
  const { dashboardCollections, selectedDashboardCollection } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const [options, setOptions] = useState<IAnalyticsMenuOption[]>();
  const [title, setTitle] = useState<string>('');
  const workspaceContext = useWorkspaceContext();

  const getCustomDashboardForUserRole = useCallback(() => {
    const customDashboardForUserRole: IComposableDashboardCollection[] = [];
    dashboardCollections.forEach((dashboard) => {
      if (dashboard.createdBy != 'Default') customDashboardForUserRole.push(dashboard);
    });
    return customDashboardForUserRole;
  }, [dashboardCollections]);

  const { owns: isAdmin } = useComposableAnalyticsDataAccessHook([]);

  useEffect(() => {
    const pathnameSplit = location.pathname.split('/');
    const section = pathnameSplit[2];
    const isDashboardAssigned = (dashboard: IComposableDashboardCollection) => {
      const listOfIds: string[] = loggedInUser?.belongsToTeams?.map((team) => team.id ?? '') ?? [];
      if (isDashboardCreator(dashboard)) {
        return `Created By: ${dashboard.createdByName}`;
      }
      if (
        dashboard.sharedWithRecruiters?.includes(workspace?.userId ?? '') ||
        (workspace?.role && dashboard.sharedWithRoles?.includes(workspace?.role))
      ) {
        return `Assigned By: ${dashboard.createdByName}`;
      }
      if (dashboard.sharedWithTeams?.find((sharedTeam) => listOfIds.includes(sharedTeam))) {
        return `Assigned By: ${dashboard.createdByName}`;
      } else return `Created By: ${dashboard.createdByName}`;
    };
    const excludedForSpecificOptions = [MAIL_ART_WORKSPACE_ID];
    const isDashboardCreator = (dashboard: IComposableDashboardCollection) =>
      dashboard.createdBy === workspace.userId || dashboard.createdBy === 'Default';
    const optionsForCreator = (dashboard: IComposableDashboardCollection) =>
      isDashboardCreator(dashboard)
        ? [
            ...(excludedForSpecificOptions.includes(workspaceContext?.id as string)
              ? []
              : [
                  {
                    text: 'Tag to Profile',
                    onClick: (index) => handleOnClick(index, 'tag'),
                  },
                ]),
            {
              text: 'Share Collection',
              onClick: (index) => handleOnClick(index, 'share'),
            },
            {
              text: 'Assign Collection',
              onClick: (index) => handleOnClick(index, 'assign'),
            },
            {
              text: 'Rename Collection',
              onClick: (index) => handleOnClick(index, 'rename'),
            },
            {
              text: 'Delete Collection',
              onClick: (index) => handleOnClick(index, 'delete'),
            },
          ]
        : [];

    const options =
      section === AnalyticsMenuItems.YOUR_DASHBOARDS_COLLECTIONS
        ? (isAdmin ? dashboardCollections : getCustomDashboardForUserRole())?.map(
            (dashboard, index) => {
              const isPinned = pinnedDashboards?.find(
                (pd) =>
                  pd.dashboardCollection.id === dashboard.id &&
                  loggedInUser?.email &&
                  pd.users?.includes(loggedInUser.email) &&
                  pd.filters == null
              );
              const isAssignedToClients = dashboard.assignToClients;
              const isAssignedToClientSpoc = dashboard.assignToClientSpoc;
              const isAssignedToAccountManagerOU = dashboard.assignToAccountManagerOU;
              const isAssignedToTeamsOU = dashboard.assignToTeamsOU;
              const isAssignedToTeams = dashboard.assignToTeams;
              const isAssignedToAccountManagers = dashboard.assignToAccountManagers;
              const isAssignedToBusinessDefinition = dashboard.assignToBusinessUnit;
              const isAssignedToClientUnit = dashboard.assignToClientUnit;
              const isAssignedToClientContactUnit = dashboard.assignToClientContactUnit;
              const isAssignedToUsers = dashboard.assignToUsers;
              const isAssignedToJobs = dashboard.assignToJobs;
              return {
                label: dashboard.name,
                icon: <InsertChartOutlinedTwoToneIcon />,
                url: '/workspace-users-and-teams/master-dashboards/view',
                caption: isDashboardAssigned(dashboard),
                showKebabMenu: true,
                kebabMenuOptions: [
                  {
                    text: isPinned ? 'Unpin from Home Page' : 'Pin To Home Page',
                    onClick: (index) =>
                      handleOnClick(
                        index,
                        isPinned ? 'unpin' : 'pin',
                        isDashboardCreator(dashboard)
                      ),
                  },
                  ...(workspaceContext.id == MAIL_ART_WORKSPACE_ID
                    ? []
                    : [
                        {
                          text: isAssignedToClients ? 'Unassign to Clients' : 'Assign to Clients',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToClients ? 'unAssignToClients' : 'assignToClients',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToClientSpoc
                            ? 'Unassign to Client Spocs'
                            : 'Assign to Client Spocs',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToClientSpoc
                                ? 'unAssignToClientSpoc'
                                : 'assignToClientSpoc',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToTeams ? 'Unassign to Teams' : 'Assign to Teams',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToTeams ? 'unAssignToTeams' : 'assignToTeams',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToAccountManagers
                            ? 'Unassign to Account Managers'
                            : 'Assign to Account Managers',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToAccountManagers
                                ? 'unAssignToAccountManagers'
                                : 'assignToAccountManagers',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToAccountManagerOU
                            ? 'Unassign to Account Units'
                            : 'Assign to Account Units',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToAccountManagerOU
                                ? 'unAssignToAccountManagerOU'
                                : 'assignToAccountManagerOU',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToTeamsOU
                            ? 'Unassign to Delivery Units'
                            : 'Assign to Delivery Units',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToTeamsOU ? 'unAssignToTeamsOU' : 'assignToTeamsOU',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToBusinessDefinition
                            ? 'Unassign to Business Units'
                            : 'Assign to Business Units',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToBusinessDefinition
                                ? 'unAssignToBusinessUnit'
                                : 'assignToBusinessUnit',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToUsers ? 'Unassign to Users' : 'Assign to Users',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToUsers ? 'unAssignToUsers' : 'assignToUsers',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToClientUnit
                            ? 'Unassign to Client Units'
                            : 'Assign to Client Units',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToClientUnit
                                ? 'unAssignToClientUnit'
                                : 'assignToClientUnit',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToClientContactUnit
                            ? 'Unassign to Client Contact Units'
                            : 'Assign to Client Contact Units',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToClientContactUnit
                                ? 'unAssignToClientContactUnit'
                                : 'assignToClientContactUnit',
                              isDashboardCreator(dashboard)
                            ),
                        },
                        {
                          text: isAssignedToJobs ? 'Unassign to Jobs' : 'Assign to Jobs',
                          onClick: (index) =>
                            handleOnClick(
                              index,
                              isAssignedToJobs ? 'unAssignToJobs' : 'assignToJobs',
                              isDashboardCreator(dashboard)
                            ),
                        },
                      ]),
                  ...optionsForCreator(dashboard),
                ],
                state: {
                  index,
                },
              };
            }
          )
        : jobAnalyticsOptions;

    setOptions(options);
    setTitle(
      section === AnalyticsMenuItems.YOUR_DASHBOARDS_COLLECTIONS
        ? 'Master Dashboards'
        : 'Jobs Analytics'
    );
  }, [
    dashboardCollections,
    getCustomDashboardForUserRole,
    handleOnClick,
    isAdmin,
    loggedInUser,
    pinnedDashboards,
    selectedDashboardCollection.assignToClients,
    workspace,
    workspaceContext.id,
  ]);

  return {
    options,
    title,
  };
};

export default useCollectionsOptions;
